<template>
    <article>
        <v-tabs v-model="tab" height="40" slider-size="2" show-arrows class="mb-4">
            <v-tab :to="{hash: 'ce'}" tab-value="ce">Community Edition</v-tab>
            <v-tab :to="{hash: 'ee'}" tab-value="ee">Enterprise Edition</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item value="ce" eager transition="none">
                <h3>Step #1: Installing ClickHouse</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo apt install -y apt-transport-https ca-certificates curl gnupg
curl -fsSL 'https://packages.clickhouse.com/rpm/lts/repodata/repomd.xml.key' | sudo gpg --dearmor -o /usr/share/keyrings/clickhouse-keyring.gpg
echo "deb [signed-by=/usr/share/keyrings/clickhouse-keyring.gpg] https://packages.clickhouse.com/deb stable main" | sudo tee /etc/apt/sources.list.d/clickhouse.list
sudo apt update
sudo DEBIAN_FRONTEND=noninteractive apt install -y clickhouse-server clickhouse-client
sudo service clickhouse-server start
                    </pre>
                </CodeSnippet>

                <h3>Step #2: Installing Prometheus</h3>

                <p>
                    Coroot requires Prometheus with support for Remote Write Receiver, which has been available since v2.25.0.
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo apt install -y prometheus
sudo service prometheus start
                    </pre>
                </CodeSnippet>

                <p>
                    Enable Remote Write Receiver in Prometheus by adding the <var>--enable-feature=remote-write-receiver</var> argument to the <var>/etc/default/prometheus</var> file:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
# Set the command-line arguments to pass to the server.
# Due to shell escaping, to pass backslashes for regexes, you need to double
# them (\\d for \d). If running under systemd, you need to double them again
# (\\\\d to mean \d), and escape newlines too.
ARGS="--enable-feature=remote-write-receiver"
                    </pre>
                </CodeSnippet>

                <p>Restart Prometheus:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo service prometheus restart
                    </pre>
                </CodeSnippet>

                <h3>Step #3: Installing Coroot</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot/main/deploy/install.sh | \
    BOOTSTRAP_PROMETHEUS_URL="http://127.0.0.1:9090" \
    BOOTSTRAP_REFRESH_INTERVAL=15s \
    BOOTSTRAP_CLICKHOUSE_ADDRESS=127.0.0.1:9000 \
    sh -
                    </pre>
                </CodeSnippet>

                <h3>Step #4: Installing coroot-node-agent</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
    COLLECTOR_ENDPOINT=http://127.0.0.1:8080 \
    SCRAPE_INTERVAL=15s \
    sh -
                    </pre>
                </CodeSnippet>

                <h3>Step #5: Accessing Coroot</h3>

                <p>
                    Access Coroot at: http://&lt;node_ip&gt;:8080.
                </p>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
/usr/bin/coroot-uninstall.sh
                    </pre>
                </CodeSnippet>

                <p>
                    Uninstall coroot-node-agent:
                </p>

                <CodeSnippet>
                        <pre v-hl.bash>
/usr/bin/coroot-node-agent-uninstall.sh
                        </pre>
                </CodeSnippet>

            </v-tab-item>

            <v-tab-item value="ee" eager transition="none">
                <v-alert color="info" outlined text class="mt-5">
                    The Enterprise Edition is a paid subscription (from $1 per CPU core/month) that offers extra features and priority support.
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <h3>Step #1: Installing ClickHouse</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo apt install -y apt-transport-https ca-certificates curl gnupg
curl -fsSL 'https://packages.clickhouse.com/rpm/lts/repodata/repomd.xml.key' | sudo gpg --dearmor -o /usr/share/keyrings/clickhouse-keyring.gpg
echo "deb [signed-by=/usr/share/keyrings/clickhouse-keyring.gpg] https://packages.clickhouse.com/deb stable main" | sudo tee /etc/apt/sources.list.d/clickhouse.list
sudo apt update
sudo DEBIAN_FRONTEND=noninteractive apt install -y clickhouse-server clickhouse-client
sudo service clickhouse-server start
                    </pre>
                </CodeSnippet>

                <h3>Step #2: Installing Prometheus</h3>

                <p>
                    Coroot requires Prometheus with support for Remote Write Receiver, which has been available since v2.25.0.
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo apt install -y prometheus
sudo service prometheus start
                    </pre>
                </CodeSnippet>

                <p>
                    Enable Remote Write Receiver in Prometheus by adding the <var>--enable-feature=remote-write-receiver</var> argument to the <var>/etc/default/prometheus</var> file:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
# Set the command-line arguments to pass to the server.
# Due to shell escaping, to pass backslashes for regexes, you need to double
# them (\\d for \d). If running under systemd, you need to double them again
# (\\\\d to mean \d), and escape newlines too.
ARGS="--enable-feature=remote-write-receiver"
                    </pre>
                </CodeSnippet>

                <p>Restart Prometheus:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
sudo service prometheus restart
                    </pre>
                </CodeSnippet>

                <h3>Step #3: Installing Coroot</h3>

                <v-alert color="info" outlined text class="mt-5">
                    To install Coroot Enterprise Edition, you'll need a license (from $1 per CPU core/month).
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <div class="subtitle-1">License key:</div>
                <v-form>
                    <v-text-field
                        v-model="license_key"
                        placeholder="COROOT-11111111-1111-1111-1111-111111111111"
                        outlined
                        dense
                    />
                </v-form>

                <CodeSnippet>
                    <pre class="hl">
curl -sfL https://raw.githubusercontent.com/coroot/coroot-ee/main/deploy/install.sh | \
    LICENSE_KEY="{{license_key || 'COROOT-LICENSE-KEY-HERE'}}" \
    BOOTSTRAP_PROMETHEUS_URL="http://127.0.0.1:9090" \
    BOOTSTRAP_REFRESH_INTERVAL=15s \
    BOOTSTRAP_CLICKHOUSE_ADDRESS=127.0.0.1:9000 \
    sh -</pre>
                </CodeSnippet>

                <h3>Step #4: Installing coroot-node-agent</h3>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
    COLLECTOR_ENDPOINT=http://127.0.0.1:8080 \
    SCRAPE_INTERVAL=15s \
    sh -
                    </pre>
                </CodeSnippet>

                <h3>Step #5: Accessing Coroot</h3>

                <p>
                    Access Coroot at: http://&lt;node_ip&gt;:8080.
                </p>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
/usr/bin/coroot-ee-uninstall.sh
                    </pre>
                </CodeSnippet>

                <p>
                    Uninstall coroot-node-agent:
                </p>

                <CodeSnippet>
                        <pre v-hl.bash>
/usr/bin/coroot-node-agent-uninstall.sh
                        </pre>
                </CodeSnippet>

            </v-tab-item>

        </v-tabs-items>
    </article>
</template>

<script>

export default {
    data() {
        return {
            license_key: '',
            tab: null,
        }
    },
    computed: {},
}
</script>

<style scoped>
</style>
